/**
 * Fallrisiko (trær mot linja) som ETT logisk lag.
 *
 * To kildeformer støttes:
 *  1. Nytt enkeltlag `fallrisiko` (foretrukket): én LineStringZ per tre (tretopp-bue → kontakt → del over linja) med
 *     norske feltnavn (treffer_linja, innenfor_krav, over_linja_m, last_indeks, kontakt_x/y/z, vurdering, …).
 *  2. Dagens tre lag `veg_fall_top_arcs` + `veg_fall_overshoot` + `veg_fall_contacts`, slått sammen i vieweren.
 *
 * Stil, funn og infokort bruker de interne (engelske) feltnavnene. ALLE oversettelser fra kildefelt står i
 * FALL_FIELD_ALIASES under – oppdater bare den når motorens endelige feltliste er kjent.
 */

export const FALL_LAYER = 'fallrisiko';
/** Dagens tre lag (reserve når `fallrisiko` mangler) */
export const FALL_PART_LAYERS = ['veg_fall_top_arcs', 'veg_fall_overshoot', 'veg_fall_contacts'] as const;

/**
 * Internt feltnavn → mulige kildefelt (i prioritert rekkefølge). Internt navn brukes selv om kilden har det
 * (dagens lag); ellers første alias som finnes. Feltene som ikke står her, vises uendret under «Alle detaljer».
 */
export const FALL_FIELD_ALIASES: Readonly<Record<string, readonly string[]>> = {
  tree_id: ['tre_id'],
  hits_geometric: ['treffer_linja'],
  hits: ['innenfor_krav'],
  height_m: ['trehoyde_m', 'tre_hoyde_m', 'hoyde_m'],
  dist_3d_m: ['avstand_3d_m', 'avstand_linja_3d_m'],
  dist_h_m: ['avstand_horisontal_m'],
  contact_fraction: ['andel_av_stamme_ved_treff', 'kontakt_andel', 'treff_andel'],
  overshoot_m: ['over_linja_m'],
  overshoot_fraction: ['over_linja_andel'],
  crown_over_line_m: ['krone_over_linja_m'],
  shortfall_m: ['kort_m', 'mangler_m', 'mangler_til_linja_m'],
  load_index: ['last_indeks'],
  clearance_m: ['minsteavstand_m', 'krav_m'],
  wire_id: ['ledning_id'],
  span_id: ['spenn_id'],
  wire_role: ['ledningsrolle'],
  fall_azimuth_deg: ['fallretning_grader'],
  voltage_level: ['spenningsniva', 'spenning'],
  label_short: ['etikett'],
  assessment: ['vurdering'],
  contact_x: ['kontakt_x'],
  contact_y: ['kontakt_y'],
  contact_z: ['kontakt_z'],
  top_x: ['topp_x'],
  top_y: ['topp_y'],
  top_z: ['topp_z'],
};

/** Kildeattributter → interne navn (originalfeltene beholdes, så «Alle detaljer» viser kilden uendret). */
export function normalizeFallProps(props: Record<string, unknown>): Record<string, unknown> {
  let out: Record<string, unknown> | null = null;
  for (const [key, aliases] of Object.entries(FALL_FIELD_ALIASES)) {
    if (props[key] !== undefined && props[key] !== null) continue;
    for (const a of aliases) {
      if (props[a] !== undefined && props[a] !== null && props[a] !== '') { (out ??= { ...props })[key] = props[a]; break; }
    }
  }
  return out ?? props;
}

export const isFallRiskLayer = (id: string): boolean => id === FALL_LAYER || (FALL_PART_LAYERS as readonly string[]).includes(id);

// ------------------------------------------------------------------ filter: avstand til treff

/**
 * Aktivt visningsfilter (modul-tilstand som `viewOptions`). s = d3 − H i meter: positivt = treet mangler s m på å nå linja,
 * negativt = treet rekker -s m forbi linja. Treet vises når d3 − H ≤ s, dvs. over_linja_m ≥ −s. 0 = bare trær som treffer.
 */
export interface FallFilter { s: number }
export const DEFAULT_FALL_FILTER: Readonly<FallFilter> = { s: 0 };
export const fallFilter: FallFilter = { ...DEFAULT_FALL_FILTER };
export const FALL_S_MAX = 10, FALL_S_MIN = -10;

const toNum = (v: unknown): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) ? n : undefined;
};
const toFlag = (v: unknown): boolean | undefined => (v === true || v === 1 || v === '1' ? true : v === false || v === 0 || v === '0' ? false : undefined);
/** Ugyldige/manglende verdier → 0. Klemmes til −10…10 i steg på 0,5. */
export function sanitizeFallFilter(v: unknown): FallFilter {
  const n = toNum(((v && typeof v === 'object' ? v : {}) as Record<string, unknown>).s);
  return { s: n === undefined ? 0 : Math.round(Math.min(FALL_S_MAX, Math.max(FALL_S_MIN, n)) * 2) / 2 };
}
export function setFallFilter(v: Partial<FallFilter>): void { Object.assign(fallFilter, sanitizeFallFilter({ ...fallFilter, ...v })); }
export const fallFilterActive = (f: FallFilter = fallFilter): boolean => f.s !== 0;

/** Øvre grense for s: sikkerhetsmarginen kjøringen brukte (største `sikkerhetsmargin_m` i laget; mangler feltet antas 0). */
export function fallMargin(features: { props: Record<string, unknown> }[]): number {
  let m = 0;
  for (const x of features) m = Math.max(m, toNum(x.props.sikkerhetsmargin_m) ?? toNum(x.props.fall_margin_m) ?? 0);
  return Math.min(FALL_S_MAX, m);
}

/** `treffer_linja` finnes? Eldre kjøringer mangler feltet (props må være normalisert). */
export const hasHitField = (p: Record<string, unknown>): boolean => toFlag(p.hits_geometric) !== undefined;

/**
 * Tegnes/telles treet? Treff: over_linja_m ≥ −s. Nesten-treff (bare når s > 0): mangler ≤ s. Uten `treffer_linja` (eldre kjøring)
 * kan treff ikke avgjøres: eldre regel (`innenfor_krav`) gjelder, uten filter.
 */
export function fallVisible(p: Record<string, unknown>, f: FallFilter = fallFilter): boolean {
  const geo = toFlag(p.hits_geometric);
  if (geo === undefined) return !!toFlag(p.hits);
  if (geo) return (toNum(p.overshoot_m) ?? 0) >= -f.s;
  const short = toNum(p.shortfall_m);
  return f.s > 0 && short !== undefined && short <= f.s;
}

/** «N av M treffer»: M = alle trær som treffer linja, N = de som vises med filteret. Null når feltet mangler. */
export function fallCounts(features: { props: Record<string, unknown> }[], f: FallFilter = fallFilter): { shown: number; hitting: number } | null {
  const ps = features.map((x) => normalizeFallProps(x.props));
  if (ps.length && !ps.some(hasHitField)) return null;
  return { shown: ps.filter((p) => fallVisible(p, f)).length, hitting: ps.filter((p) => toFlag(p.hits_geometric) === true).length };
}
export const fallCountText = (c: { shown: number; hitting: number }): string => `${c.shown} av ${c.hitting} treffer`;

const nb = (n: number) => n.toLocaleString('nb-NO', { maximumFractionDigits: 1 });
/** Filterbeskrivelse til rapport/eksport. */
export function fallFilterText(f: FallFilter = fallFilter): string {
  return f.s === 0 ? 'bare trær som treffer linja (avstand til treff 0 m)'
    : f.s > 0 ? `avstand til treff +${nb(f.s)} m (trær som mangler opptil ${nb(f.s)} m på å nå linja er med)`
    : `avstand til treff ${nb(f.s)} m (bare trær som rekker minst ${nb(-f.s)} m forbi linja)`;
}
/** Filnavn-ledd: «treffer», «avstand-pluss1-5m», «avstand-minus1m». */
export const fallFilterSlug = (f: FallFilter = fallFilter): string =>
  f.s === 0 ? 'treffer' : `avstand-${f.s > 0 ? 'pluss' : 'minus'}${nb(Math.abs(f.s)).replace(',', '-')}m`;
export const FALL_EMPTY_TEXT = 'Ingen trær treffer linja med dette filteret';
export const FALL_HELP = '1 = trær som mangler opptil 1 m på å nå linja. -1 = bare trær som rekker minst 1 m forbi linja.';
export const fallMarginHint = (m: number): string => `Kjøringen tok med trær opptil ${nb(m)} m fra å treffe.`;
export const FALL_NO_FIELD_NOTE = 'Laget mangler feltet treffer_linja (eldre kjøring). Treffilteret kan ikke brukes – kjør analysen på nytt.';
